import type * as models from '../models'

import type { Layout } from './layout'

/** Column configuration with its current calculated width. */
export interface Column<TRow = unknown> {
  readonly align: models.Column.Alignment
  readonly config: models.Column<TRow>
  readonly id: models.Column.Id
  readonly resizeMinWidthPx: number
  readonly widthPx: number
}

export namespace Column {
  /** Default width used when a column has no configured basis. */
  const DEFAULT_COLUMN_BASIS_PX = 100
  /** Minimum width used when a column has no configured minimum. */
  const DEFAULT_COLUMN_MIN_PX = 30
  /** Horizontal space consumed by the table shell outside the column tracks. */
  const HORIZONTAL_PADDING_PX = 30

  /** Intermediate values used by the width allocator before layout output. */
  interface InitialColumn<TRow> {
    readonly config: models.Column<TRow>
    readonly grow: number
    readonly id: models.Column.Id
    readonly minWidthPx: number
    readonly preferredWidthPx: number
    readonly resized: boolean
  }

  /**
   * Resolves configured columns in the current local order.
   *
   * Without a local order (the user has not reordered anything), the configured
   * order is used as-is. Otherwise, the controlled column list determines which
   * IDs are valid. Local order is reconciled against it first, so stale IDs
   * disappear and new IDs are added without allowing the settings overlay to
   * mutate the consumer config.
   */
  export function getOrdered<TRow>(
    columns: readonly models.Column<TRow>[],
    columnOrder: readonly models.Column.Id[] | undefined,
  ): readonly models.Column<TRow>[] {
    if (!columnOrder) {
      return columns
    }

    const columnsById = new Map(columns.map(column => [column.id, column]))
    const orderedColumnIds = reconcileOrder(
      columnOrder,
      columns.map(column => column.id),
    )

    return orderedColumnIds.flatMap(id => {
      const column = columnsById.get(id)
      return column ? [column] : []
    })
  }

  /**
   * Removes columns that the current controlled config marks as hidden.
   *
   * Hidden columns remain in the controlled config and local order; they are
   * omitted only from the render/layout stage so they can be shown again in
   * their previous position.
   */
  export function getVisible<TRow>(
    columns: readonly models.Column<TRow>[],
  ): readonly models.Column<TRow>[] {
    return columns.filter(column => column.visible !== false)
  }

  /**
   * Moves one configured column while retaining every stable column ID.
   * Invalid drag indexes are a no-op and return the original array reference,
   * which avoids a needless signal update after an incomplete drag.
   */
  export function reorder(
    columnOrder: readonly models.Column.Id[],
    fromIndex: number,
    toIndex: number,
  ): readonly models.Column.Id[] {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= columnOrder.length ||
      toIndex >= columnOrder.length
    ) {
      return columnOrder
    }

    const nextOrder = [...columnOrder]
    const [columnId] = nextOrder.splice(fromIndex, 1)
    if (columnId === undefined) {
      return columnOrder
    }
    nextOrder.splice(toIndex, 0, columnId)
    return nextOrder
  }

  /**
   * Keeps surviving local IDs ordered and inserts newly configured IDs right
   * after their configured predecessor (or first, when they have none).
   *
   * Placing new IDs next to their configured neighbor, rather than appending
   * them, keeps columns that arrive in a later snapshot (e.g. once async
   * attributes resolve) in their configured position after a user reorder.
   *
   * Returning `currentOrder` when the contents already match is intentional:
   * the store uses reference stability to avoid invalidating layout work when a
   * new config snapshot did not actually change the column set.
   */
  export function reconcileOrder(
    currentOrder: readonly models.Column.Id[],
    configuredOrder: readonly models.Column.Id[],
  ): readonly models.Column.Id[] {
    const configuredIds = new Set(configuredOrder)
    const nextOrder = currentOrder.filter(id => configuredIds.has(id))
    const orderedIds = new Set(nextOrder)
    // The configured predecessor is always in `nextOrder` by the time the next
    // ID is visited: it either survived or was just inserted.
    let predecessorId: models.Column.Id | undefined
    for (const id of configuredOrder) {
      if (!orderedIds.has(id)) {
        const insertAt =
          predecessorId === undefined ? 0 : nextOrder.indexOf(predecessorId) + 1
        nextOrder.splice(insertAt, 0, id)
        orderedIds.add(id)
      }
      predecessorId = id
    }

    const orderChanged = nextOrder.some(
      (id, index) => id !== currentOrder[index],
    )
    return orderChanged || nextOrder.length !== currentOrder.length
      ? nextOrder
      : currentOrder
  }

  /**
   * Removes measurements for columns absent from the current config.
   * Measurements belong to IDs, so a removed column must not leave stale width
   * data that could affect a future column with a reused local position.
   */
  export function filterWidths(
    widths: ReadonlyMap<models.Column.Id, number>,
    configuredColumnIds: ReadonlySet<models.Column.Id>,
  ): ReadonlyMap<models.Column.Id, number> {
    const filteredWidths = new Map(
      [...widths].filter(([id]) => configuredColumnIds.has(id)),
    )

    return filteredWidths.size === widths.size ? widths : filteredWidths
  }

  /**
   * Produces the synchronized layout for header and body rows.
   *
   * Before a manual resize, the calculation reserves shell, selection, and
   * row-tool space first, then allocates the remaining width to columns.
   * After a resize, stored widths remain fixed and the content width may be
   * smaller or larger than the viewport. Both header and body use the same
   * resulting grid template, keeping their tracks aligned.
   */
  export function getLayout<TRow>(
    columns: readonly models.Column<TRow>[],
    hostWidthPx: number,
    resizedWidthsPx: ReadonlyMap<models.Column.Id, number>,
    selectionWidthPx: number,
    rowToolsWidthPx: number,
  ): Layout<TRow> {
    // The viewport may be narrower than the fixed chrome. Never pass a
    // negative amount into the width allocator.
    const availableWidthPx = Math.max(
      hostWidthPx - HORIZONTAL_PADDING_PX - selectionWidthPx - rowToolsWidthPx,
      0,
    )
    // Normalize all configured and user-resized constraints before distributing
    // width, so every later stage can operate on one consistent shape.
    const initialColumns = columns.map(column =>
      getInitialColumn(column, resizedWidthsPx.get(column.id)),
    )
    const widths = getColumnWidths(initialColumns, availableWidthPx)
    const appliedColumns = initialColumns.map(
      (column, index): Column<TRow> => ({
        align: column.config.align ?? 'left',
        config: column.config,
        id: column.id,
        resizeMinWidthPx: Math.max(
          column.config.width?.minPx ?? DEFAULT_COLUMN_MIN_PX,
          0,
        ),
        widthPx: widths[index] ?? column.minWidthPx,
      }),
    )
    const columnsWidthPx = appliedColumns.reduce(
      (total, column) => total + column.widthPx,
      0,
    )
    // Content grows beyond the viewport when minimum widths cannot fit. This
    // lets the data viewport expose horizontal scrolling instead of shrinking
    // columns below their constraints.
    const contentWidthPx = Math.max(
      hostWidthPx,
      HORIZONTAL_PADDING_PX +
        selectionWidthPx +
        columnsWidthPx +
        rowToolsWidthPx,
    )
    const gridTemplateColumns = [
      ...(selectionWidthPx ? [`${selectionWidthPx}px`] : []),
      ...appliedColumns.map(column => `${column.widthPx}px`),
      ...(rowToolsWidthPx ? [`${rowToolsWidthPx}px`] : []),
    ].join(' ')

    return {
      columns: appliedColumns,
      contentWidthPx,
      gridTemplateColumns,
    }
  }

  /**
   * Normalizes configured and resized values before width distribution.
   *
   * A manual resize replaces the preferred basis but never lowers the
   * configured minimum. Non-negative growth values keep invalid config from
   * producing negative or inverted allocation ratios.
   */
  function getInitialColumn<TRow>(
    config: models.Column<TRow>,
    resizedWidthPx: number | undefined,
  ): InitialColumn<TRow> {
    const configuredMinWidthPx = Math.max(
      config.width?.minPx ?? DEFAULT_COLUMN_MIN_PX,
      0,
    )
    const minWidthPx = Math.max(
      resizedWidthPx ?? configuredMinWidthPx,
      configuredMinWidthPx,
    )

    return {
      config,
      grow: Math.max(config.width?.grow ?? 1, 0),
      id: config.id,
      minWidthPx,
      preferredWidthPx: Math.max(
        resizedWidthPx ?? config.width?.basisPx ?? DEFAULT_COLUMN_BASIS_PX,
        minWidthPx,
      ),
      resized: resizedWidthPx !== undefined,
    }
  }

  /**
   * Allocates available width across minimum and preferred column ranges.
   *
   * There are four modes:
   * - after a manual resize: every stored width stays fixed;
   * - below minimum: every column stays at its minimum and the container can
   *   scroll horizontally;
   * - between minimum and preferred: columns receive proportional partial
   *   growth toward their preferred widths;
   * - above preferred: remaining space is distributed by the configured grow
   *   weights, excluding manually resized columns when flexible columns exist.
   */
  function getColumnWidths<TRow>(
    columns: readonly InitialColumn<TRow>[],
    availableWidthPx: number,
  ): readonly number[] {
    // After the first manual resize, the store retains the calculated width of
    // every visible column. Keep those widths fixed so resizing one column does
    // not silently redistribute space to its siblings. The resulting content
    // may be narrower than the viewport or wider than it, which is intentional:
    // the data viewport handles the unused space or horizontal scrolling.
    if (columns.some(column => column.resized)) {
      return columns.map(column => column.preferredWidthPx)
    }

    const minimumWidthPx = columns.reduce(
      (total, column) => total + column.minWidthPx,
      0,
    )
    const preferredWidthPx = columns.reduce(
      (total, column) => total + column.preferredWidthPx,
      0,
    )

    if (availableWidthPx <= minimumWidthPx) {
      // The layout cannot satisfy all minimums. Preserve them exactly and let
      // the caller expose overflow through contentWidthPx.
      return columns.map(column => column.minWidthPx)
    }

    if (availableWidthPx < preferredWidthPx) {
      // Scale only the distance between each minimum and preferred width. This
      // prevents one large preferred column from consuming all available space
      // before smaller columns reach their own minimums.
      const preferredSpacePx = preferredWidthPx - minimumWidthPx
      const availablePreferredRatio =
        (availableWidthPx - minimumWidthPx) / preferredSpacePx

      return columns.map(
        column =>
          column.minWidthPx +
          (column.preferredWidthPx - column.minWidthPx) *
            availablePreferredRatio,
      )
    }

    // Once every preferred width fits, distribute spare space by grow weight.
    // User-resized columns are fixed when at least one unresized flexible
    // column can absorb the remainder.
    const flexibleColumns = columns.filter(
      column => !column.resized && column.grow > 0,
    )
    const growColumns = flexibleColumns.length ? flexibleColumns : columns
    const totalGrow = growColumns.reduce(
      (total, column) => total + column.grow,
      0,
    )
    const remainingWidthPx = availableWidthPx - preferredWidthPx

    return columns.map(column => {
      const addedWidthPx =
        growColumns.includes(column) && totalGrow > 0
          ? (remainingWidthPx * column.grow) / totalGrow
          : 0

      return column.preferredWidthPx + addedWidthPx
    })
  }
}
